<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

  </div>
</template>

<script>

import PusherClient from '@/libs/pusher'
import BeamsClient from '@/libs/beams'
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { withTenantKey } from '@/libs/storageKeys'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import login from '@/http/requests/auth/login'
import {
  decodeJwtPayload,
  isJwtExpired,
  setPendingLoginReload,
  setStoredAuthRedirectToast,
} from '@/helpers/authSession'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  data() {
    return {
      currentPusherKey: null,
      currentBeamsInstanceId: null,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    me() {
      return this.$store.getters['auth/getUser']
    },
    config() {
      return this.$store.getters['appConfig/config']
    },
    tenantId() {
      return this.$store.state.appConfig.organization.info.id
    },
  },
  watch: {
    me: {
      immediate: true,
      handler(newUser, oldUser) {
        this.handleUserChanged(newUser, oldUser)
      },
    },
    config: {
      immediate: true,
      handler() {
        this.setupRealtimeClients()
      },
    },
    tenantId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.syncBeamsSession()
      }
    },
  },
  created() {
    this.setupNotifications()
    this.attachStorageListener()
    this.attachResumeListeners()
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  beforeMount() {
    this.bootstrapApplication()
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('focus', this.handleSessionResume)
    window.removeEventListener('pageshow', this.handleSessionResume)
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    login.init(store)

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
  methods: {
    async bootstrapApplication() {
      const organizationInfoRequest = this.$store.dispatch('appConfig/fetchOrganizationInfo')
        .catch(() => {})

      const canBootstrapAuthenticatedRequests = await this.ensureSessionReadyForBootstrap()
      if (canBootstrapAuthenticatedRequests) {
        try {
          await Promise.all([
            this.$store.dispatch('auth/fetchUserInfo'),
            this.$store.dispatch('auth/fetchUserTeam'),
          ])
        } catch (error) {
          this.redirectToLoginAfterSessionExpiration()
          return
        }
      }

      organizationInfoRequest.finally(() => {
        if (this.$router.currentRoute.name === 'login' && canBootstrapAuthenticatedRequests) {
          this.$router.push('/')
        }
      })
    },
    hasStoredSession() {
      return !!localStorage.getItem(withTenantKey('userInfo')) || !!localStorage.getItem(withTenantKey('tokens'))
    },
    getStoredTokens() {
      try {
        return JSON.parse(localStorage.getItem(withTenantKey('tokens')) || 'null')
      } catch (err) {
        return null
      }
    },
    async ensureSessionReadyForBootstrap() {
      if (!this.hasStoredSession()) {
        return false
      }

      const tokens = this.getStoredTokens()
      if (!tokens?.token) {
        this.redirectToLoginAfterSessionExpiration()
        return false
      }

      if (!isJwtExpired(tokens.token)) {
        return true
      }

      try {
        const response = await login.refreshToken({ skipUnauthorizedHandler: true })
        if (response?.data?.token) {
          this.$store.commit('auth/SET_TOKEN', response.data)
          return true
        }
      } catch (error) {
        // Redirect is handled below.
      }

      this.redirectToLoginAfterSessionExpiration()
      return false
    },
    redirectToLoginAfterSessionExpiration() {
      if (this.$router.currentRoute.name !== 'login') {
        localStorage.setItem('previousPath', this.$router.currentRoute.path)
      }

      setStoredAuthRedirectToast('auth.session-expired')
      setPendingLoginReload()
      this.$store.commit('auth/REMOVE_USER_INFO_AND_TEAM')
      this.$store.commit('auth/REMOVE_USER_TOKEN')

      if (this.$router.currentRoute.name !== 'login') {
        this.$router.replace({ name: 'login' }).catch(() => {})
      }
    },
    attachStorageListener() {
      window.addEventListener('storage', this.handleStorageChange)
    },
    attachResumeListeners() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('focus', this.handleSessionResume)
      window.addEventListener('pageshow', this.handleSessionResume)
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.handleSessionResume()
      }
    },
    async handleSessionResume() {
      if (!this.hasStoredSession()) {
        return
      }

      const tokens = this.getStoredTokens()
      if (tokens?.token && isJwtExpired(tokens.token)) {
        await this.ensureSessionReadyForBootstrap()
        return
      }

      if (!this.isActiveSessionIdentityValid()) {
        this.redirectToLoginAfterSessionExpiration()
      }
    },
    isActiveSessionIdentityValid() {
      const tokens = this.getStoredTokens()
      if (!tokens?.token || isJwtExpired(tokens.token)) {
        return false
      }

      const payload = decodeJwtPayload(tokens.token)
      const tokenUserId = payload?.user_id
      if (!tokenUserId) {
        return false
      }

      const activeUserId = this.me?.id
      if (activeUserId && String(activeUserId) !== String(tokenUserId)) {
        return false
      }

      try {
        const storedUser = JSON.parse(localStorage.getItem(withTenantKey('userInfo')) || 'null')
        return !storedUser?.id || String(storedUser.id) === String(tokenUserId)
      } catch (error) {
        return false
      }
    },
    handleStorageChange(event) {
      const tokenKey = withTenantKey('tokens')

      // React only to token changes for current tenant
      if (event.key !== tokenKey) {
        return
      }

      // Token added/refreshed in another tab: sync and stay logged in
      if (event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue)
          this.$store.commit('auth/SET_TOKEN', parsed)
          return
        } catch (err) {
          // fall through to logout on parse issues
        }
      }

      // Token removed elsewhere: logout here as well
      if (!event.newValue) {
        this.$store.dispatch('auth/logout', { reason: 'expired' }).then(() => {
          this.$router.push({ name: 'login' })
        })
      }
    },
    handleUserChanged(newUser, oldUser) {
      const newId = newUser && newUser.id
      const oldId = oldUser && oldUser.id
      if (newId && newId !== oldId) {
        if (this.isExternalPartnerUser(newUser)) {
          this.setupRealtimeClients()
        } else {
          this.$store.dispatch('appConfig/fetchOrganizationConfig')
            .catch(() => {})
            .finally(() => {
              this.setupRealtimeClients()
            })
        }
      }
      this.syncBeamsSession()
    },
    isExternalPartnerUser(user) {
      const roles = user?.rawRoles || user?.roles || []
      return roles.includes('ROLE_EXTERNAL_REQUESTER') || roles.includes('ROLE_EXTERNAL_PARTNER_ADMIN')
    },
    setupRealtimeClients() {
      if (!this.config || !this.config.notifications) {
        return
      }

      const pusherKey = this.config.notifications.pusher?.key
      if (pusherKey && this.currentPusherKey !== pusherKey) {
        PusherClient.init(pusherKey)
        this.currentPusherKey = pusherKey
      }

      const beamsInstanceId = this.config.notifications.beams?.instanceId
      if (beamsInstanceId && this.currentBeamsInstanceId !== beamsInstanceId) {
        BeamsClient.init(beamsInstanceId)
        this.currentBeamsInstanceId = beamsInstanceId
      }

      this.syncBeamsSession()
    },
    syncBeamsSession() {
      if (!this.currentBeamsInstanceId) {
        return
      }
      const session = this.buildBeamsSessionDescriptor()
      if (!session) {
        return
      }
      BeamsClient.syncForCurrentSession(session).catch(err => {
        // eslint-disable-next-line no-console
        console.warn('[Beams] sync skipped:', err?.message || err)
      })
    },
    buildBeamsSessionDescriptor() {
      const { tenantId } = this
      const user = this.me
      if (!tenantId || !user || !user.id) {
        return null
      }
      return {
        tenantId,
        username: user.username,
        roles: user.rawRoles || [],
      }
    },
    setupNotifications() {
      window.toast = Object.freeze({
        notify: {
          success: (header, message) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: header,
                text: message,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          },
          warning: (header, message) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: header,
                text: message,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          },
          danger: (header, message) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: header,
                text: message,
                icon: 'XIcon',
                variant: 'danger',
              },
            })
          },
        },
      })
    },
  },
}
</script>
